import { type RichDoc, firstLine } from "@sebas/core";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/Button";
import { ApiError } from "@/lib/api";
import { authClient } from "@/lib/auth";
import { isTauri } from "@/lib/platform";
import { useCreateNote } from "@/lib/queries";

/** Survives hiding the window, quitting the app, and a failed save. */
const DRAFT_KEY = "sebas.quick-draft";

function readDraft(): string {
  try {
    return localStorage.getItem(DRAFT_KEY) ?? "";
  } catch {
    return "";
  }
}

function writeDraft(text: string): void {
  try {
    if (text) localStorage.setItem(DRAFT_KEY, text);
    else localStorage.removeItem(DRAFT_KEY);
  } catch {
    // A blocked storage backend costs the draft, not the capture.
  }
}

/** Fire-and-forget call into the Rust side; a no-op in a browser tab. */
async function native(command: string): Promise<void> {
  if (!isTauri()) return;
  const { invoke } = await import("@tauri-apps/api/core");
  await invoke(command);
}

/**
 * Plain text to a TipTap document, one paragraph per line.
 *
 * Blank lines are kept as empty paragraphs so the spacing you typed survives
 * into the full editor, where the note is opened next.
 */
function toDoc(text: string): RichDoc {
  return {
    type: "doc",
    content: text.split("\n").map((line) =>
      line.trim()
        ? { type: "paragraph", content: [{ type: "text", text: line }] }
        : { type: "paragraph" },
    ),
  };
}

type Status = { kind: "idle" | "saving" | "saved" } | { kind: "error"; message: string };

/**
 * The flying note — a frameless always-on-top window summoned with ⌘⇧N.
 *
 * Built for the one case the product cares most about: a thought arrives while
 * you are doing something else. So it opens straight into a caret, files with
 * ⌘↩, and gets out of the way with Esc. It never blocks on the network — the
 * draft is in localStorage from the first keystroke, so a failed save, a hidden
 * window or a quit all leave the text exactly where you left it.
 *
 * Deliberately not the TipTap editor: rich text here would mean toolbars and a
 * heavier mount, and this window has to be typeable the instant it appears.
 * Whatever is captured opens in the real editor afterwards.
 */
export function QuickCapturePage() {
  const { data: session, isPending } = authClient.useSession();
  const create = useCreateNote();
  const [text, setText] = useState(readDraft);
  const [status, setStatus] = useState<Status>({ kind: "idle" });
  const textarea = useRef<HTMLTextAreaElement>(null);

  const focusEditor = useCallback(() => {
    const el = textarea.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, []);

  // The window is shown and hidden rather than created and destroyed, so this
  // component does not remount on each summon — Rust emits an event instead.
  useEffect(() => {
    if (!isTauri()) return;

    let unlisten: (() => void) | undefined;
    let cancelled = false;

    void import("@tauri-apps/api/event")
      .then(({ listen }) => listen("quick-focus", focusEditor))
      .then((fn) => {
        if (cancelled) fn();
        else unlisten = fn;
      });

    return () => {
      cancelled = true;
      unlisten?.();
    };
  }, [focusEditor]);

  useEffect(() => {
    if (!isPending && session) focusEditor();
  }, [focusEditor, isPending, session]);

  // Bound to the window, not the textarea: Esc has to dismiss whatever is on
  // screen — including the signed-out and loading states, which have no
  // textarea to have been typing in.
  useEffect(() => {
    const onEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      void native("quick_hide");
    };
    window.addEventListener("keydown", onEscape);
    return () => window.removeEventListener("keydown", onEscape);
  }, []);

  const update = (value: string) => {
    setText(value);
    writeDraft(value);
    setStatus((s) => (s.kind === "error" ? { kind: "idle" } : s));
  };

  const hide = () => void native("quick_hide");

  const save = () => {
    const body = text.trim();
    // Nothing to file: treat ⌘↩ on an empty note as "dismiss".
    if (!body) {
      hide();
      return;
    }
    if (status.kind === "saving") return;

    setStatus({ kind: "saving" });
    create.mutate(
      { title: firstLine(body, 60), content: toDoc(body) },
      {
        onSuccess: () => {
          update("");
          setStatus({ kind: "saved" });
          void native("quick_saved");
          // Long enough to read "Saved", short enough not to feel like a wait.
          window.setTimeout(() => {
            setStatus({ kind: "idle" });
            hide();
          }, 550);
        },
        onError: (error) => {
          // The draft is already in localStorage, so the text is never at risk.
          setStatus({
            kind: "error",
            message:
              error instanceof ApiError && error.status === 401
                ? "Session expired — open Sebas and sign in again."
                : "Could not save. Your text is kept.",
          });
        },
      },
    );
  };

  // Esc is handled window-wide; this only carries the save chord.
  const onKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      save();
    }
  };

  return (
    // The padding is the transparent gutter the card's shadow is drawn into —
    // the native window shadow is off, since it would trace the square window
    // rather than the rounded card. `overflow: hidden` on <body> (styles/theme.css)
    // clips anything the shadow casts past this gutter, so it has to be sized to
    // the shadow's actual reach, not to taste: `shadow-card`'s 24px blur at
    // -12px spread reaches roughly 12px on the top/sides and, because it is
    // offset 8px downward, roughly 20px on the bottom. A too-small gutter (this
    // was 10px) doesn't shrink the shadow — it hard-clips it into a rectangle.
    <div className="h-dvh w-full pt-5 px-5 pb-7">
      <div className="flex h-full flex-col overflow-hidden rounded-[20px] border border-line bg-raised shadow-card">
        <div
          data-tauri-drag-region
          className="flex select-none items-center justify-between gap-2 px-4 pt-3 pb-1.5"
        >
          <span
            data-tauri-drag-region
            className="text-[11px] font-medium uppercase tracking-[0.09em] text-ink-faint"
          >
            Quick note
          </span>
          <button
            type="button"
            onClick={hide}
            aria-label="Hide the quick note"
            className="-mr-1 rounded-full px-2 py-0.5 text-[13px] leading-none text-ink-faint transition-colors hover:bg-sunken hover:text-ink"
          >
            ✕
          </button>
        </div>

        {isPending ? (
          <p className="flex-1 px-4 text-[13px] text-ink-faint">Checking your session…</p>
        ) : session ? (
          <>
            <label className="sr-only" htmlFor="quick-capture">
              Note
            </label>
            <textarea
              id="quick-capture"
              ref={textarea}
              value={text}
              onChange={(e) => update(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="What's on your mind?"
              spellCheck
              className="min-h-0 flex-1 resize-none bg-transparent px-4 pb-2 text-[15px] leading-relaxed text-ink outline-none placeholder:text-ink-faint"
            />
            <div className="flex items-center justify-between gap-3 border-t border-line px-4 py-2.5">
              <span
                className={
                  status.kind === "error"
                    ? "text-[11px] text-tile-coral"
                    : "text-[11px] text-ink-faint"
                }
              >
                {status.kind === "error"
                  ? status.message
                  : status.kind === "saved"
                    ? "Saved to Sebas"
                    : "⌘↩ save · esc hide"}
              </span>
              <Button size="sm" onClick={save} disabled={status.kind === "saving"}>
                {status.kind === "saving" ? "Saving…" : "Save"}
              </Button>
            </div>
          </>
        ) : (
          <div className="flex flex-1 flex-col items-start justify-center gap-3 px-4 pb-4">
            <p className="text-[13px] text-ink-muted">Sign in to Sebas to capture notes here.</p>
            <Button size="sm" onClick={() => void native("open_main")}>
              Open Sebas
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
